import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  Chip,
  FormControlLabel,
  Switch,
  Grid,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  CheckCircle as TestIcon,
} from '@mui/icons-material';

import axios from 'axios';

const envApiUrl = process.env.REACT_APP_API_URL;
const API_BASE = (envApiUrl && envApiUrl !== '' && envApiUrl !== 'undefined' && envApiUrl !== 'RELATIVE_URL') ? envApiUrl : '';

const EndpointManagement = ({ showNotification }) => {
  const [endpoints, setEndpoints] = useState([]);
  const [defaultEndpointId, setDefaultEndpointId] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingEndpoint, setEditingEndpoint] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    endpoint_url: '',
    port: null,
    use_ssl: false,
    timeout: 30,
    auto_register: false,
    api_base_url: 'https://dgt7.aocopt.com/api/',
    registration_endpoint: '/patientMonitors/postPatientMonitorsRegister',
    status_endpoint: '/patientMonitors/postPatientMonitorsStatusChange',
    is_default: false,
  });

  useEffect(() => {
    loadEndpoints();
  }, []);

  const loadEndpoints = async () => {
    try {
      const response = await axios.get(`${API_BASE}/api/endpoints`);
      setEndpoints(response.data.endpoints || []);
      setDefaultEndpointId(response.data.default_endpoint_id);
    } catch (error) {
      showNotification('Failed to load endpoints', 'error');
      console.error('Load error:', error);
    }
  };

  const handleOpenDialog = (endpoint = null) => {
    if (endpoint) {
      setEditingEndpoint(endpoint);
      setFormData(endpoint);
    } else {
      setEditingEndpoint(null);
      setFormData({
        name: '',
        endpoint_url: '',
        port: null,
        use_ssl: false,
        timeout: 30,
        auto_register: false,
        api_base_url: 'https://dgt7.aocopt.com/api/',
        registration_endpoint: '/patientMonitors/postPatientMonitorsRegister',
        status_endpoint: '/patientMonitors/postPatientMonitorsStatusChange',
        is_default: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingEndpoint(null);
  };

  const handleFormChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleSubmit = async () => {
    try {
      if (editingEndpoint) {
        // Update existing endpoint
        await axios.put(`${API_BASE}/api/endpoints/${editingEndpoint.id}`, formData);
        showNotification('Endpoint updated successfully', 'success');
      } else {
        // Create new endpoint
        await axios.post(`${API_BASE}/api/endpoints`, formData);
        showNotification('Endpoint created successfully', 'success');
      }
      handleCloseDialog();
      loadEndpoints();
    } catch (error) {
      showNotification(
        editingEndpoint ? 'Failed to update endpoint' : 'Failed to create endpoint',
        'error'
      );
      console.error('Submit error:', error);
    }
  };

  const handleDelete = async (endpointId) => {
    if (!window.confirm('Are you sure you want to delete this endpoint?')) {
      return;
    }

    try {
      await axios.delete(`${API_BASE}/api/endpoints/${endpointId}`);
      showNotification('Endpoint deleted successfully', 'success');
      loadEndpoints();
    } catch (error) {
      showNotification('Failed to delete endpoint', 'error');
      console.error('Delete error:', error);
    }
  };

  const handleSetDefault = async (endpointId) => {
    try {
      await axios.post(`${API_BASE}/api/endpoints/${endpointId}/set-default`);
      showNotification('Default endpoint updated', 'success');
      loadEndpoints();
    } catch (error) {
      showNotification('Failed to set default endpoint', 'error');
      console.error('Set default error:', error);
    }
  };

  const handleTestConnection = async (endpointId) => {
    try {
      const response = await axios.get(`${API_BASE}/api/endpoints/${endpointId}/test`);
      showNotification(`✓ ${response.data.message}`, 'success');
    } catch (error) {
      showNotification('Connection test failed', 'error');
      console.error('Test error:', error);
    }
  };

  return (
    <Box>
      <Box sx={{ 
        mb: 3, 
        display: 'flex', 
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 2, sm: 0 },
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' }
      }}>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontSize: { xs: '1.5rem', sm: '2rem' } }}>
            Destination Endpoints
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Manage multiple destination servers for data transmission
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          size="small"
          sx={{ width: { xs: '100%', sm: 'auto' } }}
        >
          Add Endpoint
        </Button>
      </Box>

      {endpoints.length === 0 && (
        <Alert severity="info" sx={{ mb: 3 }}>
          No endpoints configured. Click "Add Endpoint" to create your first destination server.
        </Alert>
      )}

      <Grid container spacing={3}>
        {endpoints.map((endpoint) => (
          <Grid item xs={12} key={endpoint.id}>
            <Card variant={endpoint.is_default ? 'elevation' : 'outlined'}  elevation={endpoint.is_default ? 3 : 1}>
              <CardContent>
                <Box sx={{ 
                  display: 'flex', 
                  flexDirection: { xs: 'column', sm: 'row' },
                  gap: { xs: 2, sm: 0 },
                  justifyContent: 'space-between', 
                  alignItems: { xs: 'stretch', sm: 'flex-start' }
                }}>
                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Typography variant="h6">
                        {endpoint.name}
                      </Typography>
                      {endpoint.is_default && (
                        <Chip
                          icon={<StarIcon />}
                          label="Default"
                          color="primary"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {endpoint.auto_register && (
                        <Chip
                          label="Auto-Register"
                          color="secondary"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                      {!endpoint.is_active && (
                        <Chip
                          label="Inactive"
                          color="default"
                          size="small"
                          sx={{ ml: 1 }}
                        />
                      )}
                    </Box>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="textSecondary">
                          Endpoint URL
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                          {endpoint.endpoint_url}
                        </Typography>
                      </Grid>
                      
                      {endpoint.port && (
                        <Grid item xs={6} md={3}>
                          <Typography variant="body2" color="textSecondary">
                            Port
                          </Typography>
                          <Typography variant="body1">
                            {endpoint.port}
                          </Typography>
                        </Grid>
                      )}
                      
                      <Grid item xs={6} md={3}>
                        <Typography variant="body2" color="textSecondary">
                          Timeout
                        </Typography>
                        <Typography variant="body1">
                          {endpoint.timeout}s
                        </Typography>
                      </Grid>
                      
                      {endpoint.auto_register && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="textSecondary">
                            Registration API
                          </Typography>
                          <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {endpoint.api_base_url || 'https://dgt7.aocopt.com/api/'}
                          </Typography>
                        </Grid>
                      )}
                      
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Chip
                            label={`${endpoint.devices_using} device(s) using`}
                            size="small"
                            variant="outlined"
                          />
                          {endpoint.use_ssl && (
                            <Chip label="SSL/TLS" size="small" color="success" variant="outlined" />
                          )}
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                  
                  <Box sx={{ 
                    display: 'flex', 
                    gap: 1, 
                    ml: { xs: 0, sm: 2 },
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'center', sm: 'flex-start' }
                  }}>
                    {!endpoint.is_default && (
                      <Tooltip title="Set as Default">
                        <IconButton
                          size="small"
                          onClick={() => handleSetDefault(endpoint.id)}
                        >
                          <StarBorderIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    <Tooltip title="Test Connection">
                      <IconButton
                        color="info"
                        size="small"
                        onClick={() => handleTestConnection(endpoint.id)}
                      >
                        <TestIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleOpenDialog(endpoint)}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        color="error"
                        size="small"
                        onClick={() => handleDelete(endpoint.id)}
                        disabled={endpoint.devices_using > 0}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEndpoint ? 'Edit Endpoint' : 'Add New Endpoint'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Endpoint Name"
                value={formData.name}
                onChange={(e) => handleFormChange('name', e.target.value)}
                placeholder="e.g., Production Server, Test Server, Backup Server"
                required
              />
            </Grid>
            
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Endpoint URL"
                value={formData.endpoint_url}
                onChange={(e) => handleFormChange('endpoint_url', e.target.value)}
                placeholder="tcp://dgt7.aocopt.com:6077 or ws://dgt7socket.aocopt.com"
                required
                helperText="WebSocket (ws://), Secure WebSocket (wss://), or TCP (tcp://)"
              />
            </Grid>
            
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="number"
                label="Port (optional)"
                value={formData.port || ''}
                onChange={(e) => handleFormChange('port', parseInt(e.target.value) || null)}
                helperText="Override port if not in URL"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                type="number"
                label="Connection Timeout"
                value={formData.timeout}
                onChange={(e) => handleFormChange('timeout', parseInt(e.target.value))}
                helperText="Timeout in seconds"
              />
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.use_ssl}
                    onChange={(e) => handleFormChange('use_ssl', e.target.checked)}
                  />
                }
                label="Use SSL/TLS"
              />
            </Grid>
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_default}
                    onChange={(e) => handleFormChange('is_default', e.target.checked)}
                  />
                }
                label="Set as default endpoint for new devices"
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
                DGT7 Registration Protocol
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.auto_register}
                    onChange={(e) => handleFormChange('auto_register', e.target.checked)}
                  />
                }
                label="Enable automatic device registration"
              />
              <Typography variant="caption" display="block" color="textSecondary" sx={{ ml: 4 }}>
                When enabled, devices will register via HTTP POST to DGT7 server before streaming
              </Typography>
            </Grid>
            
            {formData.auto_register && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="DGT7 API Base URL"
                    value={formData.api_base_url}
                    onChange={(e) => handleFormChange('api_base_url', e.target.value)}
                    placeholder="https://dgt7.aocopt.com/api/"
                    helperText="Base URL for DGT7 server API"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Registration Endpoint"
                    value={formData.registration_endpoint}
                    onChange={(e) => handleFormChange('registration_endpoint', e.target.value)}
                    placeholder="/patientMonitors/postPatientMonitorsRegister"
                    helperText="Path for device registration"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Status Update Endpoint"
                    value={formData.status_endpoint}
                    onChange={(e) => handleFormChange('status_endpoint', e.target.value)}
                    placeholder="/patientMonitors/postPatientMonitorsStatusChange"
                    helperText="Path for status updates"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Alert severity="info">
                    <Typography variant="body2">
                      <strong>DGT7 Registration Protocol (Database-Driven):</strong>
                    </Typography>
                    <Typography variant="caption" component="div">
                      1. POST to {formData.api_base_url}{formData.registration_endpoint}
                    </Typography>
                    <Typography variant="caption" component="div">
                      2. Receive WebSocket connection details from server
                    </Typography>
                    <Typography variant="caption" component="div">
                      3. Connect to server-provided WebSocket endpoint
                    </Typography>
                    <Typography variant="caption" component="div">
                      4. Send "deviceSocket" identifier
                    </Typography>
                    <Typography variant="caption" component="div">
                      5. Stream real-time data
                    </Typography>
                  </Alert>
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            color="primary"
            disabled={!formData.name || !formData.endpoint_url}
          >
            {editingEndpoint ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EndpointManagement;

